import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { APP_FOOTER_CONFIG, type FooterLink } from './app-footer.config';
import { COMPANY_INFO } from './company-info';
import { resolveFooterLinks } from './footer-links';

@Component({
  selector: 'app-footer',
  imports: [RouterLink],
  template: `
    <footer
      class="border-t border-gray-200 bg-white text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400"
    >
      <div class="grid w-full grid-cols-1 gap-8 px-4 py-8 sm:px-6 md:grid-cols-3 lg:px-8">
        <!-- min-w-0 — 그리드 아이템의 기본 min-width:auto가 내용의 min-content로 트랙을 밀어내
          1fr 균등분배를 깨는 것을 막는다(실측: 주소·사업자번호 등 안 끊기는 텍스트 때문에
          1열이 트랙 대부분을 가져가고 가운데 열이 0으로 붕괴했었다). -->
        <div class="min-w-0 space-y-1">
          <p class="font-medium text-gray-800 dark:text-gray-100">{{ companyInfo.name }}</p>
          <p>{{ companyInfo.ceo }}</p>
          <p>{{ companyInfo.callNumber }}</p>
          <p>{{ companyInfo.businessRegistrationNumber }}</p>
          <p>{{ companyInfo.mailOrderBusinessNumber }}</p>
          <p>
            <a
              [href]="companyInfo.addressMapUrl"
              target="_blank"
              rel="noopener"
              class="hover:text-gray-700 dark:hover:text-gray-300 no-underline"
              >{{ companyInfo.address }}</a
            >
          </p>
          <p class="pt-2 text-gray-500 dark:text-gray-400">Copyright © {{ companyInfo.name }}</p>
        </div>

        @if (links().length > 0) {
          <!--
            md:col-start-3 — 3열 그리드에서 사이트맵을 명시적으로 3번째 칸에 놓아, 가운데
            칸은 비워 둔 채(회사 정보와 사이트맵 사이 여백) 좌/우로 나뉘게 한다. 모바일
            (grid-cols-1)에서는 col-start가 적용되지 않아 회사 정보 아래로 자연스럽게 쌓인다.
            그룹 제목은 렌더링하지 않는다(2026-08-12 확정) — 그룹당 링크가 1~2건뿐이라
            제목이 과했다.
          -->
          <nav class="min-w-0 md:col-start-3" aria-label="사이트맵">
            <ul class="space-y-1">
              @for (link of links(); track link.label) {
                <li>
                  @if (link.routerLink !== null && link.routerLink !== undefined) {
                    <a [routerLink]="link.routerLink" class="hover:text-gray-700 dark:hover:text-gray-300 no-underline"
                      >{{ link.label }}</a
                    >
                  } @else {
                    <a [href]="link.href" class="hover:text-gray-700 dark:hover:text-gray-300 no-underline">{{
                      link.label
                    }}</a>
                  }
                </li>
              }
            </ul>
          </nav>
        }
      </div>
    </footer>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppFooterComponent {
  private readonly config = inject(APP_FOOTER_CONFIG, { optional: true }) ?? {};

  protected readonly companyInfo = COMPANY_INFO;

  /**
   * 그룹 제목은 렌더링에 쓰지 않는다(2026-08-12 사용자 확인) — 링크가 그룹당 1~2건뿐인
   * 앱이 많아 제목이 과했다. `AppFooterConfig.linkGroups`의 `title`은 여러 앱이 이어붙일
   * 링크를 구성할 때 소스를 구분하기 위한 값으로만 남기고, 화면에는 평평한 링크 목록만
   * 보여준다.
   */
  protected readonly links = computed<readonly FooterLink[]>(() => resolveFooterLinks(this.config));
}
